import { BayLevelEnum, } from "open-vessel-definition";
import { CellInfo } from "./CellInfo";
import { getBayLevelRows, normalizeLashingBridgeInfo, } from "../lashingBridges/lashingBridgesHelpers";
import { pad2, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";
import { EIGHTANDHALF_FEET_IN_MM } from "./consts";
import { bayLevelHasCenterLineRow } from "../bayLevelHasCenterLineRow";
import { SmartSlotsData } from "../slotsData/SmartSlotsData";
export class BaySizesAndCgs {
    constructor(bl, sizeSummary, masterCGs) {
        var _a, _b, _c, _d, _e, _f;
        this.bl = bl;
        this.minTier = Infinity;
        this.maxTier = -Infinity;
        this.maxRow = 0;
        this.centerLineRow = 0;
        this.cellsInfo = new Map();
        /**
         * Stack bottom of each row of the bay-level: its defined bottom base (row, common or master CGs)
         * and its bottom tier. Rows without slots get the bottom base defined in `perRowInfo`, if any.
         */
        this.rowBottoms = new Map();
        /** Defined TCG of each row of the bay-level (row info or master CGs), including rows without slots */
        this.tcgByRow = new Map();
        this.missingImportantLcgs = false;
        this.missingImportantVcgs = false;
        this.missingImportantTcgs = false;
        this.countLcgs = 0;
        this.definedLcgs = 0;
        this.countTcgs = 0;
        this.definedTcgs = 0;
        this.countBottomBase = 0;
        this.definedBottomBase = 0;
        this.pairedToBay = undefined;
        this.someTierInPairedBayHas40 = false;
        this.maxBottomBase = -Infinity;
        this.minBottomBase = Infinity;
        this.minLcg = Infinity;
        this.maxLcg = -Infinity;
        this.minTcg = Infinity;
        this.maxTcg = -Infinity;
        this.minSize = Infinity;
        this.maxSize = 0;
        this.getAvgCGs = (row) => {
            var _a, _b;
            if (!this.sizes || this.sizes.length === 0)
                return undefined;
            const lcgsBy2040Size = new Map();
            let countLcgLessThan40 = 0;
            let countLcgGreaterOrEqualThan40 = 0;
            let tcgAnySize = 0;
            let countTcgAnySize = 0;
            let lowestBottomBaseAnySize = Infinity;
            const avgCellInfo = this.sizes.reduce((acc, size) => {
                let lcg = 0;
                let tcg = 0;
                let bottomBase = 0;
                let countLcg = 0;
                let countTcg = 0;
                let countBottomBase = 0;
                this.slotsKeys
                    .filter((pos) => !row || pos.substring(0, 2) === row)
                    .forEach((pos) => {
                    var _a, _b;
                    const posInfo = this.cellsInfo.get(pos);
                    const slotLcg = posInfo === null || posInfo === void 0 ? void 0 : posInfo.getLcg(size);
                    const slotTcg = posInfo === null || posInfo === void 0 ? void 0 : posInfo.getTcg(size);
                    const slotBottomBase = posInfo === null || posInfo === void 0 ? void 0 : posInfo.getBottomBase(size);
                    if (slotLcg !== undefined) {
                        lcg += slotLcg;
                        countLcg += 1;
                        if (size < 40) {
                            lcgsBy2040Size.set(20, ((_a = lcgsBy2040Size.get(20)) !== null && _a !== void 0 ? _a : 0) + slotLcg);
                            countLcgLessThan40 += 1;
                        }
                        else {
                            lcgsBy2040Size.set(40, ((_b = lcgsBy2040Size.get(40)) !== null && _b !== void 0 ? _b : 0) + slotLcg);
                            countLcgGreaterOrEqualThan40 += 1;
                        }
                    }
                    if (slotTcg !== undefined) {
                        tcg += slotTcg;
                        countTcg += 1;
                        tcgAnySize += slotTcg;
                        countTcgAnySize += 1;
                    }
                    if (slotBottomBase !== undefined) {
                        bottomBase += slotBottomBase;
                        countBottomBase += 1;
                        if (slotBottomBase < lowestBottomBaseAnySize)
                            lowestBottomBaseAnySize = slotBottomBase;
                    }
                });
                if (countLcg + countTcg + countBottomBase === 0)
                    return acc;
                acc.set(size, {
                    size,
                    lcg: lcg / (countLcg || 1),
                    tcg: tcg / (countTcg || 1),
                    bottomBase: bottomBase / (countBottomBase || 1),
                    bottomTier: this.minTier !== undefined ? pad2(this.minTier) : undefined,
                    stackBottomBase: Number.isFinite(lowestBottomBaseAnySize) ? lowestBottomBaseAnySize : 0,
                    isoOddBay: this.isoBay,
                    bayLevel: this.level,
                });
                return acc;
            }, new Map());
            return {
                perSize: avgCellInfo,
                isoOddBay: this.isoBay,
                lowestBottomBase: lowestBottomBaseAnySize,
                lowestBottomTier: this.minTier !== undefined ? pad2(this.minTier) : undefined,
                avgTcg: tcgAnySize / (countTcgAnySize || 1),
                avgLcg20: ((_a = lcgsBy2040Size.get(20)) !== null && _a !== void 0 ? _a : 0) / (countLcgLessThan40 || 1),
                avgLcg40: ((_b = lcgsBy2040Size.get(40)) !== null && _b !== void 0 ? _b : 0) / (countLcgGreaterOrEqualThan40 || 1),
            };
        };
        this.isoBay = bl.isoBay;
        this.level = bl.level;
        this.bayLabel = bl.label20 || bl.label40;
        this.pairedBay = bl.pairedBay;
        this.bulkhead = bl.bulkhead;
        this.lashingBridges = normalizeLashingBridgeInfo(bl.lashingBridges);
        this.maxHeight = Math.max((_c = (_b = (_a = bl.perRowInfo) === null || _a === void 0 ? void 0 : _a.common) === null || _b === void 0 ? void 0 : _b.maxHeight) !== null && _c !== void 0 ? _c : 0, Math.max(...Object.values(((_d = bl.perRowInfo) === null || _d === void 0 ? void 0 : _d.each) || {}).map((r) => { var _a; return (_a = r.maxHeight) !== null && _a !== void 0 ? _a : 0; })));
        this.sizes = [];
        const { infoByContLength, perSlotInfo, perRowInfo } = bl;
        const commonRowInfo = perRowInfo === null || perRowInfo === void 0 ? void 0 : perRowInfo.common;
        const perRowInfoEach = perRowInfo === null || perRowInfo === void 0 ? void 0 : perRowInfo.each;
        const smartSlotsData = new SmartSlotsData(Object.values(perSlotInfo || {}));
        const rowTcgsAll = {};
        const { sizes, definedSlots } = smartSlotsData.getAllSizesInSet();
        const { rows: rowsFromSlots, tiersByRow: tiersByRowFromSlots, minTier, maxTier, } = smartSlotsData.getRowsAndTiers();
        this.slotsKeys = smartSlotsData.getSlotsKeys();
        this.sizes = sizes || [];
        this.definedSlots = definedSlots;
        this.minTier = Number(minTier);
        this.maxTier = Number(maxTier);
        this.maxRow = Math.max(...rowsFromSlots.map((r) => Number(r)));
        this.centerLineRow = (_f = (_e = bl.centerLineRow) !== null && _e !== void 0 ? _e : sizeSummary.centerLineRow) !== null && _f !== void 0 ? _f : 0;
        this.hasCenterLineRowForTcgs = bayLevelHasCenterLineRow(sizeSummary, bl);
        const cellsInfo = smartSlotsData.getSmartSlotsData().reduce((acc, sSlotData) => {
            var _a, _b, _c, _d;
            const pos = sSlotData.pos;
            const row = sSlotData.row;
            const tier = sSlotData.tier;
            if (sSlotData.data.restricted) {
                acc.set(pos, new CellInfo({ row, tier, restricted: 1 }));
                return acc;
            }
            const isAbove = bl.level === BayLevelEnum.ABOVE;
            const slotSizes = sSlotData.allSizes;
            const currentRowInfo = perRowInfoEach === null || perRowInfoEach === void 0 ? void 0 : perRowInfoEach[row];
            const bottomIsoTier = ((_a = tiersByRowFromSlots[row]) === null || _a === void 0 ? void 0 : _a.minTier)
                ? pad2(tiersByRowFromSlots[row].minTier)
                : undefined;
            // Bottom base is the one defined in the row, or in common, or in masterCGs
            const bottomBase = (_c = (_b = currentRowInfo === null || currentRowInfo === void 0 ? void 0 : currentRowInfo.bottomBase) !== null && _b !== void 0 ? _b : commonRowInfo === null || commonRowInfo === void 0 ? void 0 : commonRowInfo.bottomBase) !== null && _c !== void 0 ? _c : (bottomIsoTier !== undefined ? masterCGs.bottomBases[bottomIsoTier] : undefined);
            const tcg = (_d = currentRowInfo === null || currentRowInfo === void 0 ? void 0 : currentRowInfo.tcg) !== null && _d !== void 0 ? _d : (isAbove ? masterCGs.aboveTcgs[row] : masterCGs.belowTcgs[row]);
            if (!this.rowBottoms.has(row))
                this.rowBottoms.set(row, { bottomBase, bottomTier: bottomIsoTier });
            const infoBySize = slotSizes.reduce((acc, size) => {
                var _a, _b, _c, _d;
                const obj = {
                    size,
                    lcg: (_c = (_b = (_a = currentRowInfo === null || currentRowInfo === void 0 ? void 0 : currentRowInfo.rowInfoByLength) === null || _a === void 0 ? void 0 : _a[size]) === null || _b === void 0 ? void 0 : _b.lcg) !== null && _c !== void 0 ? _c : (_d = infoByContLength === null || infoByContLength === void 0 ? void 0 : infoByContLength[size]) === null || _d === void 0 ? void 0 : _d.lcg,
                    tcg,
                    bottomBase,
                    stackBottomBase: bottomBase || 0,
                    bottomTier: bottomIsoTier,
                    extending: this.pairedBay,
                    isoOddBay: this.isoBay,
                    bayLevel: this.level,
                };
                if (!rowTcgsAll[row])
                    rowTcgsAll[row] = [tcg];
                else
                    rowTcgsAll[row].push(tcg);
                this.countLcgs += 1;
                if (obj.lcg !== undefined) {
                    if (this.minLcg > obj.lcg)
                        this.minLcg = obj.lcg;
                    if (this.maxLcg < obj.lcg)
                        this.maxLcg = obj.lcg;
                    this.definedLcgs += 1;
                }
                this.countTcgs += 1;
                if (obj.tcg !== undefined) {
                    if (this.minTcg > obj.tcg)
                        this.minTcg = obj.tcg;
                    if (this.maxTcg < obj.tcg)
                        this.maxTcg = obj.tcg;
                    this.definedTcgs += 1;
                }
                this.countBottomBase += 1;
                if (obj.bottomBase !== undefined) {
                    this.definedBottomBase += 1;
                    if (this.minBottomBase > obj.bottomBase)
                        this.minBottomBase = obj.bottomBase;
                    if (this.maxBottomBase < obj.bottomBase)
                        this.maxBottomBase = obj.bottomBase;
                }
                if (size > this.maxSize)
                    this.maxSize = size;
                if (size < this.minSize)
                    this.minSize = size;
                acc[`${size}`] = obj;
                return acc;
            }, {});
            const result = new CellInfo({
                row,
                tier,
                pairedBay: this.pairedBay,
                maxSize: sSlotData.maxSize,
                sizes: infoBySize,
                isReefer: sSlotData.data.reefer,
            });
            acc.set(pos, result);
            return acc;
        }, new Map());
        this.missingImportantLcgs = this.definedLcgs < this.countLcgs;
        this.missingImportantTcgs = this.definedTcgs < this.countTcgs;
        this.missingImportantVcgs = this.definedBottomBase < this.countBottomBase;
        this.cellsInfo = cellsInfo;
        const rowsTcgs = new Map();
        const perRowAddedUpTcgDeviations = new Map();
        Object.keys(rowTcgsAll).forEach((row) => {
            const filteredRow = rowTcgsAll[row].filter((v) => !isNaN(v));
            const tcgsSum = filteredRow.reduce((acc, v) => acc + v, 0);
            const tcgsCount = filteredRow.length;
            if (tcgsCount) {
                const avgTcg = tcgsSum / tcgsCount;
                rowsTcgs.set(row, avgTcg);
                perRowAddedUpTcgDeviations.set(row, filteredRow.reduce((acc, v) => acc + Math.abs(avgTcg - v), 0));
            }
        });
        this.perRowAverageBayTcg = rowsTcgs;
        this.perRowAddedUpTcgDeviations = perRowAddedUpTcgDeviations;
        // Rows without slots (or restricted ones) still need a TCG and a bottom base for the lashing bridges
        const isAboveLevel = bl.level === BayLevelEnum.ABOVE;
        getBayLevelRows(sizeSummary, bl).forEach((row) => {
            var _a, _b, _c, _d;
            const rowInfo = perRowInfoEach === null || perRowInfoEach === void 0 ? void 0 : perRowInfoEach[row];
            const tcg = (_a = rowInfo === null || rowInfo === void 0 ? void 0 : rowInfo.tcg) !== null && _a !== void 0 ? _a : (isAboveLevel ? (_b = masterCGs.aboveTcgs) === null || _b === void 0 ? void 0 : _b[row] : (_c = masterCGs.belowTcgs) === null || _c === void 0 ? void 0 : _c[row]);
            if (tcg !== undefined)
                this.tcgByRow.set(row, tcg);
            if (!this.rowBottoms.has(row)) {
                const bottomBase = (_d = rowInfo === null || rowInfo === void 0 ? void 0 : rowInfo.bottomBase) !== null && _d !== void 0 ? _d : commonRowInfo === null || commonRowInfo === void 0 ? void 0 : commonRowInfo.bottomBase;
                if (bottomBase !== undefined)
                    this.rowBottoms.set(row, { bottomBase });
            }
        });
        // console.log("maxHeight",
        //   this.isoBay,
        //   BayLevelEnum[this.level],
        //   this.maxTier, this.minTier,
        //   Math.floor((this.maxTier - this.minTier) * 0.5) + 1,
        //   this.getDeckMaxTheoreticalHeight(true),
        //   this.maxHeight,
        // );
    }
    /** Gets the cell info for a given position
     * @warning This method should be used only internally. Use the function {@link getContainersCgs} passing a whole set of containers instead.
     *  Because it will return the cell info for a given position, but it will not include missing CGs.
     */
    getCellInfo(pos) {
        return this.cellsInfo.get(pos);
    }
    /** Gets all the tiers in the deck, ordered ascending */
    getAllTiers() {
        const tiers = [];
        this.cellsInfo.forEach((s) => {
            tiers.push(s.tier);
        });
        return tiers.filter((v, i, a) => a.indexOf(v) === i).sort(sortNumericAsc);
    }
    /** Gets the number of tiers in the deck */
    getNumberOfTiers() {
        if (this.maxTier === -Infinity || this.minTier === Infinity)
            return 0;
        return Math.floor((this.maxTier - this.minTier) * 0.5) + 1;
    }
    /** Gets the max size in the tiers */
    getMaxSizeInTiers(whichTiers) {
        const maxSizeInTiers = [];
        this.cellsInfo.forEach((s) => {
            if ((!whichTiers || whichTiers.includes(s.tier)) && s.maxSize)
                maxSizeInTiers.push(s.maxSize);
        });
        return Math.max(...maxSizeInTiers);
    }
    /** Gets the minimum bottom base of the current deck */
    getDeckBottomBase() {
        return this.minBottomBase === Infinity ? undefined : this.minBottomBase;
    }
    /** Returns theoretical height for the given container height adding up the minBottomBase (via {@link getDeckBottomBase}) */
    getDeckMaxTheoreticalAbsoluteHeight(containerHeightInMm = EIGHTANDHALF_FEET_IN_MM) {
        var _a;
        const minBottomBase = this.getDeckBottomBase();
        if (!minBottomBase)
            return undefined;
        return ((_a = this.getDeckMaxTheoreticalHeight(containerHeightInMm)) !== null && _a !== void 0 ? _a : 0) + minBottomBase;
    }
    /** Returns theoretical height, i.e. the number of tiers by the given container height in mm */
    getDeckMaxTheoreticalHeight(containerHeightInMm = EIGHTANDHALF_FEET_IN_MM) {
        return this.getNumberOfTiers() * containerHeightInMm;
    }
    isMissingPairingButPlausible() {
        return this.pairedBay === undefined && this.sizes.some((s) => s >= 40);
    }
    get bayData() {
        return this.bl;
    }
}
